import { use } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import { ScenarioType } from 'shared/enums/scenario-type';
import KeyValuePair from 'shared/models/key-value-pair';

import { FilterValueType } from 'features/residents/models/resident-settings-filter-value';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type ResidentSettingsFilters = {
  scenarioFilterOptions: FilterChipOption[];
  selectedScenarioFilterOptions: FilterChipOption[];
  linkedDeviceFilterOptions: KeyValuePair[];
  initialSelectedFilterOption: KeyValuePair | undefined;
  shouldShowWanderingDetectionFilter: boolean;
  wanderingBoundaryFilterOptions: FilterChipOption[] | undefined;
  selectedWanderingBoundaryFilterOptions: FilterChipOption[] | undefined;
  scenariosMutedFilterOptions: KeyValuePair[];
  initialSelectedScenariosMutedFilterOption: KeyValuePair | undefined;
};
const useResidentSettingsFilters = (): ResidentSettingsFilters => {
  const { t } = useTranslation('residentsSettings');

  const {
    filtersState: { activeFilters, residentFilters },
  } = use(ResidentSettingsProviderContext);

  const scenarioFilterOptions: FilterChipOption[] = [
    {
      key: ScenarioType.CurrentLocation,
      label: t('filterValues.scenario.currentLocation'),
      value: [ScenarioType.CurrentLocation],
    },
    {
      key: ScenarioType.Bed,
      label: t('filterValues.scenario.bed'),
      value: [ScenarioType.Bed],
    },
    {
      key: ScenarioType.Room,
      label: t('filterValues.scenario.room'),
      value: [ScenarioType.Room],
    },
    {
      key: ScenarioType.Bathroom,
      label: t('filterValues.scenario.bathroom'),
      value: [ScenarioType.Bathroom],
    },
    {
      key: ScenarioType.Intruder,
      label: t('filterValues.scenario.intruder'),
      value: [ScenarioType.Intruder],
    },
    {
      key: ScenarioType.FallDetection,
      label: t('filterValues.scenario.fallDetection'),
      value: [ScenarioType.FallDetection],
    },
    {
      key: ScenarioType.Door,
      label: t('filterValues.scenario.door'),
      value: [ScenarioType.Door],
    },
    {
      key: ScenarioType.Audio,
      label: t('filterValues.scenario.audio'),
      value: [ScenarioType.Audio],
    },
    {
      key: ScenarioType.Acoustic,
      label: t('filterValues.scenario.acoustic'),
      value: [ScenarioType.Acoustic],
    },
    {
      key: ScenarioType.Video,
      label: t('filterValues.scenario.video'),
      value: [ScenarioType.Video],
    },
    {
      key: ScenarioType.WanderingDetection,
      label: t('filterValues.scenario.wanderingDetection'),
      value: [ScenarioType.WanderingDetection],
    },
    {
      key: ScenarioType.None,
      label: t('filterValues.scenario.none'),
      value: [ScenarioType.None],
    },
  ];

  const selectedScenarioFilterOptions = scenarioFilterOptions.filter((x) =>
    activeFilters.find((x) => x.filterValueType === FilterValueType.Scenario)?.values.includes(x.key),
  );

  const wanderingBoundaryFilterOptions = (residentFilters?.wanderingBoundaryFilterOptions ?? []).map(
    (item) =>
      ({
        key: item.id,
        label: item.name,
        value: [item.id],
      }) as FilterChipOption,
  );

  const selectedWanderingBoundaryFilterOptions = wanderingBoundaryFilterOptions?.filter((x) =>
    activeFilters.find((activeFilter) => activeFilter.filterValueType === FilterValueType.WanderingBoundary)?.values.includes(x.key),
  );

  const shouldShowWanderingDetectionFilter = selectedScenarioFilterOptions.some((item) => item.key === ScenarioType.WanderingDetection);

  const linkedDeviceFilterOptions: KeyValuePair[] = [
    {
      key: true.toString(),
      value: t('filterValues.linkedDevice.linked'),
    },
    {
      key: false.toString(),
      value: t('filterValues.linkedDevice.notLinked'),
    },
  ];

  const initialSelectedFilterOption = linkedDeviceFilterOptions.find((x) =>
    activeFilters.find((x) => x.filterValueType === FilterValueType.LinkedDevice)?.values.includes(x.key),
  );

  const scenariosMutedFilterOptions: KeyValuePair[] = [
    {
      key: 'MutedAbsence',
      value: t('filterValues.scenariosMuted.mutedAbsence'),
    },
    {
      key: 'MutedVisitor',
      value: t('filterValues.scenariosMuted.mutedVisitor'),
    },
    {
      key: 'ScenariosActive',
      value: t('filterValues.scenariosMuted.scenariosActive'),
    },
  ];

  const initialSelectedScenariosMutedFilterOption = scenariosMutedFilterOptions.find((x) =>
    activeFilters.find((x) => x.filterValueType === FilterValueType.ScenariosMuted)?.values.includes(x.key),
  );

  return {
    scenarioFilterOptions,
    selectedScenarioFilterOptions,
    linkedDeviceFilterOptions,
    initialSelectedFilterOption,
    shouldShowWanderingDetectionFilter,
    wanderingBoundaryFilterOptions,
    selectedWanderingBoundaryFilterOptions,
    scenariosMutedFilterOptions,
    initialSelectedScenariosMutedFilterOption,
  };
};

export default useResidentSettingsFilters;
